import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dog, Scissors, Sparkles, Heart, Award, Clock, MapPin, Phone, Instagram, Star, Calendar, Package, Shield, Users, User, Edit3, Save, X, ChevronLeft, ChevronRight, MessageCircle, Truck, BookOpen, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePets } from '../contexts/PetsContext';
import useConfirmation from '../hooks/useConfirmation';
import ConfirmationModal from '../components/ConfirmationModal';
import GalleryImageModal from '../components/GalleryImageModal';
import ImageCropModal from '../components/ImageCropModal';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db, storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import Portal from '../components/Portal';
import WhatsAppModal from '../components/WhatsAppModal';
import { HomeStructuredData } from '../components/StructuredData';
import useSEO from '../hooks/useSEO';
import { useBlogPostsAggregate } from '../utils/useBlogPostsAggregate';

// Componente de texto editável (fora do Home para não ser recriado)
const EditableText = React.memo(({ value, onChange, className = "", tag = "span", multiline = false, inputClassName = "", isEditMode }) => {
  if (!isEditMode) {
    const Tag = tag;
    const viewClassName = multiline ? `${className} whitespace-pre-wrap`.trim() : className;
    return <Tag className={viewClassName}>{value}</Tag>;
  }

  const baseInputClass = inputClassName || "bg-white/10 border-2 border-white/30 rounded px-2 py-1 focus:outline-none focus:border-white/60";
  const darkInputClass = "bg-card-light/50 dark:bg-card-dark/50 border-2 border-border-light dark:border-border-dark rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500";

  if (multiline) {
    return (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${className} ${inputClassName ? baseInputClass : (className.includes('text-white') || className.includes('text-blue') ? baseInputClass : darkInputClass)} resize-none w-full`}
        rows={multiline === true ? 3 : multiline}
      />
    );
  }

  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`${className} ${inputClassName ? baseInputClass : (className.includes('text-white') || className.includes('text-blue') ? baseInputClass : darkInputClass)} w-full`}
    />
  );
});

EditableText.nomeExibicao = 'EditableText';

const Home = () => {
  const navigate = useNavigate();
  useSEO({
    title: 'Aruk Dog - Banho e Tosa em Curitiba',
    description: 'Aruk Dog: banho e tosa com carinho em Curitiba. Cuidamos do seu pet com amor, profissionalismo e dedicação.',
    canonical: '/',
    ogType: 'website',
    ogImage: '/Preto.png'
  });
  const { user } = useAuth();
  const { confirmation, showConfirmation, confirmAction, cancelAction } = useConfirmation();
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(true);
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const galleryScrollRef = useRef(null);
  // Usar agregados para reduzir leituras do Firebase
  const { posts: allBlogPosts, loading: loadingBlogPosts } = useBlogPostsAggregate();
  
  // Filtrar apenas os 3 posts mais recentes
  const blogPosts = allBlogPosts.slice(0, 3);
  const loadingBlog = loadingBlogPosts;
  
  // Função auxiliar para aplicar dados dos textos
  const applyTextsData = useCallback((data) => {
    if (!data) return;
    
    setHeroTitle(data.heroTitle || 'Aruk Dog');
    setHeroSubtitle(data.heroSubtitle || 'Banho e Tosa com Carinho');
    setHeroDescription(data.heroDescription || 'Cuidamos do seu melhor amigo com amor, profissionalismo e dedicação. Aqui cada pet é tratado como membro da família!');
    setHighlight1Title(data.highlight1Title || 'Atendimento Rápido');
    setHighlight1Desc(data.highlight1Desc || 'Serviço ágil e eficiente, sem perder a qualidade');
    setHighlight2Title(data.highlight2Title || 'Cuidado Especial');
    setHighlight2Desc(data.highlight2Desc || 'Cada pet recebe atenção personalizada e carinhosa');
    setHighlight3Title(data.highlight3Title || 'Profissionais Qualificados');
    setHighlight3Desc(data.highlight3Desc || 'Equipe treinada e apaixonada por animais');
    setQualificationsTitle(data.qualificationsTitle || 'Por Que Nos Escolher?');
    setQualificationsDesc(data.qualificationsDesc || 'Somos referência em cuidados pet com profissionalismo e amor');
    if (data.qualifications) setQualifications(data.qualifications);
    setTestimonialsTitle(data.testimonialsTitle || 'O Que Dizem Nossos Clientes');
    setTestimonialsDesc(data.testimonialsDesc || 'Depoimentos reais de tutores satisfeitos com nosso trabalho');
    if (data.testimonials && data.testimonials.length > 0) setTestimonials(data.testimonials);
    setCtaTitle(data.ctaTitle || 'Pronto para Transformar o Dia do Seu Pet?');
    setCtaDesc(data.ctaDesc || 'Agende agora mesmo e garanta o melhor cuidado para seu melhor amigo');
    setOwnerName(data.ownerName || 'Ana Paula Silva');
    setOwnerRole(data.ownerRole || 'Fundadora e Groomer Profissional');
    setOwnerBio(data.ownerBio || 'Com mais de 5 anos de experiência em banho e tosa, dedico minha vida ao cuidado e bem-estar dos pets. Cada animal que passa por aqui recebe todo meu carinho e atenção, como se fosse meu próprio pet.');
    setOwnerPhoto(data.ownerPhoto || null);
    setOwnerPhotoPath(data.ownerPhotoPath || null);
    setContactPhone(formatDisplayPhone(data.contactPhone || ''));
    setContactEmail(data.contactEmail || 'contato@seuvet.com');
    setContactAddress(data.contactAddress || 'Rua Paraiba, 2660 - Guaíra, Curitiba - PR');
    if (data.certifications) setCertifications(data.certifications);
    setOpeningHoursTitle(data.openingHoursTitle || 'Horário de Funcionamento');
    if (data.openingHours) setOpeningHours(data.openingHours);
    setGalleryTitle(data.galleryTitle || 'Galeria de Clientes');
    setGalleryDesc(data.galleryDesc || 'Veja alguns dos nossos clientes satisfeitos após nossos cuidados');
    setContactTitle(data.contactTitle || 'Entre em Contato');
    setContactDesc(data.contactDesc || 'Estamos prontos para atender você e seu pet');
    setBlogTitle(data.blogTitle || 'Blog Aruk Dog');
    setBlogDesc(data.blogDesc || 'Dicas, cuidados e novidades sobre o mundo pet');
    setPartnersTitle(data.partnersTitle || 'Nossos Parceiros');
    setPartnersDesc(data.partnersDesc || 'Empresas e profissionais que colaboram conosco para oferecer o melhor serviço');
    if (data.partners && data.partners.length > 0) setPartners(data.partners);
  }, []);

  // Estados para o modal de WhatsApp
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);

  // Estados para textos editáveis - Hero Section
  const [heroTitle, setHeroTitle] = useState('Aruk Dog');
  const [heroSubtitle, setHeroSubtitle] = useState('Banho & Tosa');
  const [heroDescription, setHeroDescription] = useState('Cuidados do seu melhor amigo com amor, profissionalismo e dedicação. Aqui cada pet é tratado como membro da família!');

  // Estados para Destaques
  const [highlight1Title, setHighlight1Title] = useState('Atendimento Rápido');
  const [highlight1Desc, setHighlight1Desc] = useState('Serviço ágil e eficiente, sem perder a qualidade');
  const [highlight2Title, setHighlight2Title] = useState('Cuidado Especial');
  const [highlight2Desc, setHighlight2Desc] = useState('Cada pet recebe atenção personalizada e carinhosa');
  const [highlight3Title, setHighlight3Title] = useState('Profissionais Qualificados');
  const [highlight3Desc, setHighlight3Desc] = useState('Equipe treinada e apaixonada por animais');

  // Estados para Seção de Serviços

  // Estados para Plano de Assinatura
  const [subscriptionTitle, setSubscriptionTitle] = useState('Plano por Assinatura');
  const [subscriptionSubtitle, setSubscriptionSubtitle] = useState('Mantenha seu pet limpinho e economize utilizando um pacote de banhos mensais');
  const [subscriptionIncludes, setSubscriptionIncludes] = useState([
    'Banho completo',
    'Hidratação',
    'Tosa higiênica'
  ]);
  const [subscriptionNote, setSubscriptionNote] = useState('Consulte os preços e condições.');

  // Estados para Call to Action
  const [ctaTitle, setCtaTitle] = useState('Pronto para Transformar o Dia do Seu Pet?');
  const [ctaDesc, setCtaDesc] = useState('Agende agora mesmo e garanta o melhor cuidado para seu melhor amigo');

  // Estados para Sobre a Dona
  const [ownerName, setOwnerName] = useState('Ana Paula Silva');
  const [ownerRole, setOwnerRole] = useState('Fundadora e Groomer Profissional');
  const [ownerBio, setOwnerBio] = useState('Com mais de 5 anos de experiência em banho e tosa, dedico minha vida ao cuidado e bem-estar dos pets. Cada animal que passa por aqui recebe todo meu carinho e atenção, como se fosse meu próprio pet.');
  const [ownerPhoto, setOwnerPhoto] = useState(null);
  const [ownerPhotoPath, setOwnerPhotoPath] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);

  // Estados para Contato
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('contato@seuvet.com');
  const [contactAddress, setContactAddress] = useState('Rua Paraiba, 2660 - Guaíra, Curitiba - PR');

  // Estados para Certificações
  const [certifications, setCertifications] = useState([
    { title: 'Certificação em Pet Grooming', institution: 'Instituto Brasileiro de Pet Grooming (2019)' },
    { title: 'Especialização em Tosas Específicas', institution: 'Academia de Tosas Avançadas (2020)' },
    { title: 'Primeiros Socorros Pet', institution: 'Cruz Vermelha Animal (2021)' }
  ]);

  // Estados para Horário de Funcionamento
  const [openingHoursTitle, setOpeningHoursTitle] = useState('Horário de Funcionamento');
  const [openingHours, setOpeningHours] = useState([
    { days: 'Segunda a Sexta', hours: '9h às 18h' },
    { days: 'Sábado', hours: '8h às 14h' },
    { days: 'Domingo e Feriados', hours: 'Fechado' }
  ]);


  // Estados para Qualificações (Por Que Nos Escolher?)
  const [qualifications, setQualifications] = useState([
    { title: 'Certificação Profissional', description: 'Formação completa em Pet Grooming e Estética Animal' },
    { title: '5+ Anos de Experiência', description: 'Mais de 5 anos dedicados ao cuidado e bem-estar animal' },
    { title: 'Amor pelos Animais', description: 'Tratamos cada pet com carinho, respeito e dedicação' },
    { title: '500+ Pets Atendidos', description: 'Centenas de clientes satisfeitos e pets felizes' }
  ]);

  // Estados para Depoimentos
  const [testimonials, setTestimonials] = useState([
    { nome: 'Maria Silva', text: 'Excelente atendimento! Meu Rex sempre volta feliz e cheiroso. A equipe é super carinhosa e profissional.', rating: 5 },
    { nome: 'João Santos', text: 'A melhor escolha que fiz! A Mel fica linda depois da tosa e o ambiente é super limpo e seguro.', rating: 5 },
    { nome: 'Ana Costa', text: 'Confiança total! Deixo meu Bob tranquila sabendo que ele está em boas mãos. Recomendo muito!', rating: 5 },
    { nome: 'Pedro Oliveira', text: 'Serviço impecável! Minha Luna sempre volta linda e cheirosa. A equipe demonstra muito carinho pelos pets.', rating: 5 },
    { nome: 'Carla Mendes', text: 'Profissionalismo de primeira! O Thor adora ir para o banho e tosa. Super recomendo para todos!', rating: 5 },
    { nome: 'Rafael Souza', text: 'Atenção diferenciada! Minha Mimi recebe cuidados especiais e sempre sai linda. Equipe muito competente.', rating: 5 },
    { nome: 'Juliana Ferreira', text: 'Ambiente super limpo e organizado! Meu Max sempre volta cheiroso e bem cuidado. Excelente trabalho!', rating: 5 },
    { nome: 'Fernando Lima', text: 'Tratamento VIP para os pets! Minha Princesa adora ir ao salão. Qualidade e carinho incríveis.', rating: 5 },
    { nome: 'Patricia Alves', text: 'O melhor banho e tosa da cidade! Meu Toby sempre volta feliz e cheiroso. Profissionalismo e dedicação.', rating: 5 },
    { nome: 'Marcos Costa', text: 'Serviço de excelência! Minha Bella fica linda depois da tosa. Confiança total na equipe!', rating: 5 }
  ]);

  // Títulos das seções
  const [qualificationsTitle, setQualificationsTitle] = useState('Por Que Nos Escolher?');
  const [qualificationsDesc, setQualificationsDesc] = useState('Somos referência em cuidados pet com profissionalismo e amor');
  const [testimonialsTitle, setTestimonialsTitle] = useState('O Que Dizem Nossos Clientes');
  const [testimonialsDesc, setTestimonialsDesc] = useState('Depoimentos reais de tutores satisfeitos com nosso trabalho');
  
  // Estados para Galeria de Clientes
  const [galleryTitle, setGalleryTitle] = useState('Galeria de Clientes');
  const [galleryDesc, setGalleryDesc] = useState('Veja alguns dos nossos clientes satisfeitos após nossos cuidados');
  
  // Estados para Entre em Contato
  const [contactTitle, setContactTitle] = useState('Entre em Contato');
  const [contactDesc, setContactDesc] = useState('Estamos prontos para atender você e seu pet');
  
  // Estados para Blog
  const [blogTitle, setBlogTitle] = useState('Blog Aruk Dog');
  const [blogDesc, setBlogDesc] = useState('Dicas, cuidados e novidades sobre o mundo pet');
  
  // Estados para Parceiros/Colaboradores
  const [partnersTitle, setPartnersTitle] = useState('Nossos Parceiros');
  const [partnersDesc, setPartnersDesc] = useState('Empresas e profissionais que colaboram conosco para oferecer o melhor serviço');
  const [partners, setPartners] = useState([
    { nome: 'Pet Shop Amigo', tipo: 'Pet Shop', descricao: 'Parceiro em produtos e acessórios para pets', logo: null, logoPath: null, site: 'https://example.com' },
    { nome: 'Veterinária Cuidar', tipo: 'Clínica Veterinária', descricao: 'Parceira em cuidados veterinários e consultas', logo: null, logoPath: null, site: 'https://example.com' }
  ]);
  const [uploadingPartnerLogo, setUploadingPartnerLogo] = useState({});
  const [partnerImageToCrop, setPartnerImageToCrop] = useState(null);
  const [partnerCropIndex, setPartnerCropIndex] = useState(null);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };


  // Funções memoizadas para atualizar arrays sem perder foco

  const updateQualification = useCallback((index, field, value) => {
    setQualifications(prev => {
      const newQuals = [...prev];
      newQuals[index] = { ...newQuals[index], [field]: value };
      return newQuals;
    });
  }, []);

  const updateTestimonial = useCallback((index, field, value) => {
    setTestimonials(prev => {
      const newTests = [...prev];
      newTests[index] = { ...newTests[index], [field]: value };
      return newTests;
    });
  }, []);

  const updateCertification = useCallback((index, field, value) => {
    setCertifications(prev => {
      const newCerts = [...prev];
      newCerts[index] = { ...newCerts[index], [field]: value };
      return newCerts;
    });
  }, []);

  const updateOpeningHour = useCallback((index, field, value) => {
    setOpeningHours(prev => {
      const newHours = [...prev];
      newHours[index] = { ...newHours[index], [field]: value };
      return newHours;
    });
  }, []);

  const updatePartner = useCallback((index, field, value) => {
    setPartners(prev => {
      const newPartners = [...prev];
      newPartners[index] = { ...newPartners[index], [field]: value };
      return newPartners;
    });
  }, []);

  const addPartner = useCallback(() => {
    setPartners(prev => [...prev, { 
      nome: 'Nova Empresa', 
      tipo: 'Tipo de Parceria', 
      descricao: 'Descrição da parceria', 
      logo: null, 
      logoPath: null,
      site: 'https://example.com'
    }]);
  }, []);

  const removePartner = useCallback(async (index) => {
    const confirmed = await showConfirmation({
      title: 'Remover Parceiro',
      messidade: 'Tem certeza que deseja remover este parceiro?',
      confirmText: 'Sim, Remover',
      cancelText: 'Cancelar',
      type: 'danger'
    });

    if (!confirmed) return;

    const partner = partners[index];
    if (partner.logoPath) {
      try {
        const logoRef = ref(storage, partner.logoPath);
        await deleteObject(logoRef);
      } catch (error) {
        // Erro ao deletar logo (pode não existir)
      }
    }

    setPartners(prev => prev.filter((_, i) => i !== index));
  }, [partners, showConfirmation]);

  // Função para fazer upload do logo do parceiro
  const handlePartnerLogoUpload = async (event, index) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      await showConfirmation({
        title: 'Arquivo Inválido',
        messidade: 'Por favor, selecione uma imagem válida.',
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      await showConfirmation({
        title: 'Arquivo Muito Grande',
        messidade: 'A imagem deve ter no máximo 15MB. Por favor, escolha uma imagem menor.',
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
      return;
    }

    setPartnerImageToCrop(file);
    setPartnerCropIndex(index);
    setShowCropModal(true);
    event.target.value = '';
  };

  // Função para processar a imagem cortada do parceiro
  const handlePartnerCropComplete = async (croppedFile) => {
    if (partnerCropIndex === null) return;

    setUploadingPartnerLogo(prev => ({ ...prev, [partnerCropIndex]: true }));
    setShowCropModal(false);

    try {
      const partner = partners[partnerCropIndex];
      
      // Deletar logo antigo se existir
      if (partner.logoPath) {
        try {
          const oldLogoRef = ref(storage, partner.logoPath);
          await deleteObject(oldLogoRef);
        } catch (error) {
          // Erro ao deletar logo antigo
        }
      }

      // Fazer upload do novo logo
      const timestamp = Date.now();
      const fileName = `partner_${partnerCropIndex}_${timestamp}_${croppedFile.name}`;
      const logoPath = `home/partners/${fileName}`;
      const logoRef = ref(storage, logoPath);
      
      await uploadBytes(logoRef, croppedFile);
      const downloadURL = await getDownloadURL(logoRef);
      
      updatePartner(partnerCropIndex, 'logo', downloadURL);
      updatePartner(partnerCropIndex, 'logoPath', logoPath);
      
      await showConfirmation({
        title: 'Sucesso!',
        message: 'Logo atualizado com sucesso!',
        confirmText: 'OK',
        showCancel: false
      });
    } catch (error) {
      await showConfirmation({
        title: 'Erro no Upload',
        messidade: 'Ocorreu um erro ao fazer upload do logo. Por favor, tente novamente.',
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
    } finally {
      setUploadingPartnerLogo(prev => ({ ...prev, [partnerCropIndex]: false }));
      setPartnerImageToCrop(null);
      setPartnerCropIndex(null);
    }
  };

  // Função para remover logo do parceiro
  const handlePartnerLogoRemove = async (index) => {
    const confirmed = await showConfirmation({
      title: 'Remover Logo',
      messidade: 'Tem certeza que deseja remover o logo deste parceiro?',
      confirmText: 'Sim, Remover',
      cancelText: 'Cancelar',
      type: 'danger'
    });

    if (!confirmed) return;

    const partner = partners[index];
    try {
      if (partner.logoPath) {
        const logoRef = ref(storage, partner.logoPath);
        await deleteObject(logoRef);
      }
      updatePartner(index, 'logo', null);
      updatePartner(index, 'logoPath', null);
      
      await showConfirmation({
        title: 'Sucesso!',
        message: 'Logo removido com sucesso!',
        confirmText: 'OK',
        showCancel: false
      });
    } catch (error) {
      await showConfirmation({
        title: 'Erro',
        messidade: 'Ocorreu um erro ao remover o logo. Por favor, tente novamente.',
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
    }
  };

  // Função para fazer upload da foto da fundadora
  const handleOwnerPhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      await showConfirmation({
        title: 'Arquivo Inválido',
        messidade: 'Por favor, selecione uma imagem válida.',
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
      return;
    }

    // Validar tamanho (máximo 15MB)
    if (file.porte > 15 * 1024 * 1024) {
      await showConfirmation({
        title: 'Arquivo Muito Grande',
        messidade: 'A imagem deve ter no máximo 15MB. Por favor, escolha uma imagem menor.',
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
      return;
    }

    // Abrir modal de crop
    setImageToCrop(file);
    setShowCropModal(true);
    
    // Limpar input
    event.target.value = '';
  };

  // Função para processar a imagem cortada
  const handleCropComplete = async (croppedFile) => {
    // Verificar se é logo de parceiro ou foto da fundadora
    if (partnerCropIndex !== null) {
      await handlePartnerCropComplete(croppedFile);
      return;
    }

    setUploadingPhoto(true);
    setShowCropModal(false);

    try {
      // Deletar foto antiga se existir
      if (ownerPhotoPath) {
        try {
          const oldPhotoRef = ref(storage, ownerPhotoPath);
          await deleteObject(oldPhotoRef);
        } catch (error) {
          // Erro ao deletar foto antiga (pode não existir)
        }
      }

      // Fazer upload da nova foto cortada
      const timestamp = Date.now();
      const fileName = `owner_photo_${timestamp}_${croppedFile.name}`;
      const photoPath = `home/owner/${fileName}`;
      const photoRef = ref(storage, photoPath);
      
      await uploadBytes(photoRef, croppedFile);
      const downloadURL = await getDownloadURL(photoRef);
      
      setOwnerPhoto(downloadURL);
      setOwnerPhotoPath(photoPath);
      
      await showConfirmation({
        title: 'Sucesso!',
        message: '',
        confirmText: 'OK',
        showCancel: false
      });
    } catch (error) {
      await showConfirmation({
        title: 'Erro no Upload',
        messidade: 'Ocorreu um erro ao fazer upload da foto. Por favor, tente novamente.',
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
    } finally {
      setUploadingPhoto(false);
      setImageToCrop(null);
    }
  };

  // Função para cancelar o crop
  const handleCropCancel = () => {
    setShowCropModal(false);
    setImageToCrop(null);
    setPartnerImageToCrop(null);
    setPartnerCropIndex(null);
  };

  // Função para remover foto da fundadora
  const handleOwnerPhotoRemove = async () => {
    const confirmed = await showConfirmation({
      title: 'Remover Foto',
      messidade: 'Tem certeza que deseja remover a foto da fundadora?',
      confirmText: 'Sim, Remover',
      cancelText: 'Cancelar',
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      if (ownerPhotoPath) {
        // Deletar do Storage
        try {
          const photoRef = ref(storage, ownerPhotoPath);
          await deleteObject(photoRef);
        } catch (error) {
          // Erro ao deletar foto do storage (pode não existir)
        }
      }

      setOwnerPhoto(null);
      setOwnerPhotoPath(null);
      
      await showConfirmation({
        title: 'Sucesso!',
        message: '',
        confirmText: 'OK',
        showCancel: false
      });
    } catch (error) {
      await showConfirmation({
        title: 'Erro',
        messidade: 'Ocorreu um erro ao remover a foto. Por favor, tente novamente.',
        confirmText: 'OK',
        showCancel: false,
        type: 'danger'
      });
    }
  };

  const { pets, loading: loadingPets } = usePets();

  // Carregar fotos da galeria dos pets usando dados do contexto
  useEffect(() => {
    // Se ainda está carregando os pets, aguardar
    if (loadingPets) {
      return;
    }

    // Quando o carregamento dos pets terminar, processar as imagens
    setLoadingGallery(true);
    
    if (pets && pets.length > 0) {
      // galeria: null=sem fotos, true=tem fotos, string=URL da imagem com estrela
      const petsWithGalleryImages = pets
        .filter(pet => typeof pet.galeria === 'string')
        .map(pet => ({
          id: pet.id,
          nome: pet.nome,
          url: pet.galeria
        }));
      
      setGalleryImages(petsWithGalleryImages);
    } else {
      // Se não houver pets, definir array vazio
      setGalleryImages([]);
    }
    
    setLoadingGallery(false);
  }, [pets, loadingPets]);

  // Posts do blog agora são carregados via agregados (hooks acima)

  // Carregar textos do Firebase em tempo real
  useEffect(() => {
    // Listener em tempo real para os textos da página inicial
    const docRef = doc(db, 'textosInicio', 'content');
    
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        try {
          if (snapshot.exists()) {
            const data = snapshot.data();
            applyTextsData(data);
          }
        } catch (error) {
          // Erro ao processar dados
        }
      },
      (error) => {
        // Callback de erro do onSnapshot
      }
    );

    // Cleanup: remover listener quando o componente desmontar
    return () => {
      unsubscribe();
    };
  }, [applyTextsData]);

  // Salvar textos no Firebase
  const handleSaveTexts = async () => {
    const confirmed = await showConfirmation({
      title: 'Salvar alterações?',
      message: 'As alterações serão publicadas na página inicial. Deseja continuar?',
      confirmText: 'Sim, salvar',
      cancelText: 'Cancelar',
      type: 'info'
    });
    if (!confirmed) return;

    try {
      const docRef = doc(db, 'textosInicio', 'content');
      const textsData = {
        // Hero Section
        heroTitle,
        heroSubtitle,
        heroDescription,
        // Destaques
        highlight1Title,
        highlight1Desc,
        highlight2Title,
        highlight2Desc,
        highlight3Title,
        highlight3Desc,
        // Serviços
        // Qualificações
        qualificationsTitle,
        qualificationsDesc,
        qualifications,
        // Depoimentos
        testimonialsTitle,
        testimonialsDesc,
        testimonials,
        // Call to Action
        ctaTitle,
        ctaDesc,
        // Sobre a Dona
        ownerName,
        ownerRole,
        ownerBio,
        ownerPhoto,
        ownerPhotoPath,
        // Contato
        contactPhone,
        contactEmail,
        contactAddress,
        // Certificações
        certifications,
        // Horário de Funcionamento
        openingHoursTitle,
        openingHours,
        // Galeria de Clientes
        galleryTitle,
        galleryDesc,
        // Entre em Contato
        contactTitle,
        contactDesc,
        // Blog
        blogTitle,
        blogDesc,
        // Parceiros/Colaboradores
        partnersTitle,
        partnersDesc,
        partners,
        // Metadados
        atualizadoEm: new Date().toISOString(),
        atualizadoPor: user?.email || 'unknown'
      };
      
      await setDoc(docRef, textsData);
      
      // O listener em tempo real atualizará automaticamente
      
      setIsEditMode(false);
    } catch (error) {
      await showConfirmation({
        title: 'Erro',
        message: 'Ocorreu um erro ao salvar os textos. Por favor, tente novamente.',
        confirmText: 'OK',
        showCancel: false
      });
    }
  };

  // Cancelar edição e recarregar dados do Firebase
  const handleCancelEdit = async () => {
    const confirmed = await showConfirmation({
      title: 'Descartar Alterações?',
      messidade: 'Todas as alterações não salvas serão perdidas. Deseja continuar?',
      confirmText: 'Sim, Descartar',
      cancelText: 'Não, Continuar Editando'
    });

    if (!confirmed) return;

    // O listener em tempo real atualizará automaticamente com os dados do Firestore
    setIsEditMode(false);
  };

  const qualificationIcons = [Award, Shield, Heart, Users];
  const testimonialEmojis = ['🐕', '🐩', '🐶', '🦮', '🐕‍🦺', '🎀', '🐺', '🐕', '🐾', '❤️'];

  const placeholderGallery = [
    { emoji: '🐕', alt: 'Golden Retriever após banho' },
    { emoji: '🐩', alt: 'Poodle com tosa estilosa' },
    { emoji: '🐶', alt: 'Shih Tzu recém banhado' },
    { emoji: '🦮', alt: 'Labrador feliz' },
    { emoji: '🐕‍🦺', alt: 'Pastor Alemão' },
    { emoji: '🎀', alt: 'Maltês com laço' },
    { emoji: '🐺', alt: 'Husky após banho' },
    { emoji: '🐾', alt: 'Patinhas felizes de cliente satisfeito' }
  ];

  useEffect(() => {
    // Desabilitar alternância automática quando estiver em modo de edição
    if (isEditMode) {
      return;
    }
    
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length, isEditMode]);

  // Funções para scrollar a galeria
  const scrollGalleryLeft = () => {
    if (galleryScrollRef.current) {
      galleryScrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollGalleryRight = () => {
    if (galleryScrollRef.current) {
      galleryScrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  // Funções para o modal da galeria
  const handleGalleryImageClick = (image) => {
    setSelectedGalleryImage(image);
    setShowGalleryModal(true);
  };

  const closeGalleryModal = () => {
    setShowGalleryModal(false);
    setSelectedGalleryImage(null);
  };

  const navigateGalleryImage = (direction) => {
    if (!selectedGalleryImage || galleryImages.length === 0) return;
    
    const currentIndex = galleryImages.findIndex(img => img.id === selectedGalleryImage.id);
    let newIndex;
    
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : galleryImages.length - 1;
    } else {
      newIndex = currentIndex < galleryImages.length - 1 ? currentIndex + 1 : 0;
    }
    
    setSelectedGalleryImage(galleryImages[newIndex]);
  };

  // Funções para abrir links de contato
  function formatDisplayPhone(phone) {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length <= 2) {
      return digits;
    }
    if (digits.length <= 6) {
      return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    }
    // Até 10 ou mais dígitos: DDD + número com hífen nos 4 últimos
    return `${digits.slice(0, 2)} ${digits.slice(2, digits.length - 4)}-${digits.slice(-4)}`;
  }

  const formatWhatsAppNumber = (phone) => {
    if (!phone) return '';
    // Remover espaços e caracteres especiais do telefone
    const phoneNumber = phone.replace(/\D/g, '');
    if (!phoneNumber) return '';
    // Adicionar código do país se não tiver (assumindo Brasil)
    return phoneNumber.startsWith('55') ? phoneNumber : `55${phoneNumber}`;
  };

  const handleOpenWhatsApp = () => {
    const fullPhoneNumber = formatWhatsAppNumber(contactPhone);
    if (!fullPhoneNumber) return;
    const whatsappUrl = `https://wa.me/${fullPhoneNumber}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleOpenInstagram = () => {
    // Remover @ se tiver no início, espaços e caracteres especiais
    let instagramHandle = contactEmail.replace(/^@/, '').trim();
    // Remover espaços
    instagramHandle = instagramHandle.replace(/\s/g, '');
    const instagramUrl = `https://instagram.com/${instagramHandle}`;
    window.open(instagramUrl, '_blank');
  };

  const handleOpenGoogleMaps = () => {
    // Criar link do Google Maps com o endereço
    const encodedAddress = encodeURIComponent(contactAddress);
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    window.open(mapsUrl, '_blank');
  };

  // Funções para o modal de WhatsApp
  const handleOpenWhatsAppModal = (e) => {
    if (e) {
      e.preventDefault();
    }
    setShowWhatsAppModal(true);
  };


  return (
    <div className="min-h-screen" style={{ backgroundColor: '#BFC9C8' }}>
      <HomeStructuredData />
      {/* Botões de Editar/Salvar/Cancelar (apenas para usuários logados) */}
      {user && (
        <div
          className="fixed right-2 sm:right-4 z-40 flex flex-col gap-2"
          style={{ top: 'var(--home-edit-top, 80px)' }}
        >
          {isEditMode ? (
            <>
              <button
                onClick={handleSaveTexts}
                className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium shadow-lg transition-all duration-200 bg-green-600 hover:bg-green-700 text-white"
              >
                <Save className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Salvar</span>
              </button>
              <button
                onClick={handleCancelEdit}
                className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium shadow-lg transition-all duration-200 bg-red-600 hover:bg-red-700 text-white"
              >
                <span>✕</span>
                <span className="hidden sm:inline">Cancelar</span>
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditMode(true)}
              className="btn-primary flex items-center space-x-1 sm:space-x-2 shadow-lg text-sm sm:text-base px-2 sm:px-4"
            >
              <Edit3 className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Editar</span>
            </button>
          )}
        </div>
      )}

      {/* Hero Section */}
      <section className="relative text-white pt-6 pb-12 px-4 overflow-hidden z-0" style={{ 
        backgroundColor: '#104347',
        backgroundImage: 'radial-gradient(circle, rgba(255, 255, 255, 0.15) 1px, transparent 1px)',
        backgroundSize: '30px 30px'
      }}>
        
        {/* Botão de Login discreto no canto */}
        {!user && (
          <div className="absolute top-4 right-2 sm:right-4 z-10">
            <button
              onClick={() => {
                if (!isEditMode) {
                  navigate('/login');
                }
              }}
              className="bg-white/90 backdrop-blur-sm text-primary-600 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium hover:bg-white transition-all duration-200 shadow-md hover:shadow-lg flex items-center space-x-1 sm:space-x-1.5"
            >
              <User className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Área do funcionário</span>
              <span className="sm:hidden">Login</span>
            </button>
          </div>
        )}
        
        <div className="max-w-6xl mx-auto relative z-0">
          <div className="text-center space-y-4">
            <div className="flex justify-center -mb-10">
              <img 
                src="/Branco.png" 
                alt="Aruk Dog Logo" 
                className="w-32 h-32 sm:w-40 sm:h-40 md:w-60 md:h-60"
              />
            </div>
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-4 px-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              <EditableText 
                value={heroTitle} 
                onChange={setHeroTitle} 
                className="text-3xl sm:text-5xl md:text-6xl font-bold"
                tag="span"
                isEditMode={isEditMode}
              />
            </h1>
            <p className="text-xl sm:text-2xl md:text-3xl font-light !mb-10 px-2">
              <EditableText 
                value={heroSubtitle} 
                onChange={setHeroSubtitle} 
                className="text-xl sm:text-2xl md:text-3xl font-light"
                tag="span"
                isEditMode={isEditMode}
              />
            </p>
            <div className="text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-8 text-white px-4">
              <EditableText 
                value={heroDescription} 
                onChange={setHeroDescription} 
                className="text-base sm:text-lg md:text-xl text-white w-full max-w-2xl mx-auto"
                tag="p"
                multiline={true}
                isEditMode={isEditMode}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center px-4">
              <button
                onClick={() => {
                  if (!isEditMode) {
                    handleOpenWhatsAppModal();
                  }
                }}
                className="w-full sm:w-auto bg-white text-primary-600 px-6 sm:px-8 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg"
              >
                <Calendar className="w-5 h-5" />
                <span>Agendar Agora</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Destaques */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center space-y-3">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: '#5CC1B3' }}>
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-black">
                <EditableText 
                  value={highlight1Title} 
                  onChange={setHighlight1Title} 
                  className="text-xl font-bold text-black"
                      tag="span"
                      isEditMode={isEditMode}
                      />
              </h3>
              <div className="text-black">
                <EditableText 
                  value={highlight1Desc} 
                  onChange={setHighlight1Desc} 
                  className="text-black"
                      tag="p"
                      isEditMode={isEditMode}
                      />
              </div>
            </div>
            <div className="text-center space-y-3">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: '#5CC1B3' }}>
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-black">
                <EditableText 
                  value={highlight2Title} 
                  onChange={setHighlight2Title} 
                  className="text-xl font-bold text-black"
                      tag="span"
                      isEditMode={isEditMode}
                      />
              </h3>
              <div className="text-black">
                <EditableText 
                  value={highlight2Desc} 
                  onChange={setHighlight2Desc} 
                  className="text-black"
                      tag="p"
                      isEditMode={isEditMode}
                      />
              </div>
            </div>
            <div className="text-center space-y-3">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: '#5CC1B3' }}>
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-black">
                <EditableText 
                  value={highlight3Title} 
                  onChange={setHighlight3Title} 
                  className="text-xl font-bold text-black"
                      tag="span"
                      isEditMode={isEditMode}
                      />
              </h3>
              <div className="text-black">
                <EditableText 
                  value={highlight3Desc} 
                  onChange={setHighlight3Desc} 
                  className="text-black"
                      tag="p"
                      isEditMode={isEditMode}
                      />
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Plano de Assinatura */}
      <section className="py-12 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="mb-4 px-2">
              <span className="inline-block px-6 py-2 rounded-full text-2xl sm:text-3xl md:text-4xl font-bold text-white" style={{ backgroundColor: '#5CC1B3' }}>
                <EditableText 
                  value={subscriptionTitle} 
                  onChange={setSubscriptionTitle} 
                  className="text-2xl sm:text-3xl md:text-4xl font-bold text-white"
                  tag="span"
                  isEditMode={isEditMode}
                />
              </span>
            </h2>
            <div className="text-base sm:text-lg text-black px-2 mb-4">
              <EditableText 
                value={subscriptionSubtitle} 
                onChange={setSubscriptionSubtitle} 
                className="text-base sm:text-lg text-black"
                tag="span"
                isEditMode={isEditMode}
              />
            </div>
          </div>

          <div className="rounded-lg shadow-lg border-2 p-6 sm:p-8" style={{ 
            backgroundColor: '#F4F7F6',
            borderColor: 'rgba(92, 193, 179, 0.5)'
          }}>
            <div className="space-y-4">
              <div className="mb-6">
                <h3 className="text-lg font-bold text-black mb-3">Inclui:</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {subscriptionIncludes.map((item, index) => (
                    <div key={index} className="flex items-center justify-center space-x-2 bg-white rounded-lg px-3 py-2 shadow-sm text-center">
                      <span className="text-primary-600 flex-shrink-0" style={{ color: '#5CC1B3' }}>✓</span>
                      <span className="text-black text-sm">
                        {isEditMode ? (
                          <input
                            type="text"
                            value={item}
                            onChange={(e) => {
                              const newIncludes = [...subscriptionIncludes];
                              newIncludes[index] = e.target.value;
                              setSubscriptionIncludes(newIncludes);
                            }}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-center"
                          />
                        ) : (
                          item
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-gray-300">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-black">
                    <EditableText 
                      value={subscriptionNote} 
                      onChange={setSubscriptionNote} 
                      className="text-sm text-black"
                      tag="span"
                      multiline={2}
                      isEditMode={isEditMode}
                    />
                  </p>
                  <button
                    onClick={() => {
                      if (!isEditMode) {
                        const message = "Olá! Gostaria de saber mais sobre o plano de banhos por assinatura.";
                        const fullPhoneNumber = formatWhatsAppNumber(contactPhone);
                        if (!fullPhoneNumber) return;
                        const whatsappUrl = `https://wa.me/${fullPhoneNumber}?text=${encodeURIComponent(message)}`;
                        window.open(whatsappUrl, '_blank');
                      }
                    }}
                    className="px-4 py-2 bg-primary-600 text-white rounded-full text-sm font-medium hover:bg-primary-700 transition-colors whitespace-nowrap"
                    style={{ backgroundColor: '#5CC1B3' }}
                  >
                    Consultar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Galeria de Fotos */}
      <section className="py-12 sm:py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="mb-4 px-2">
              <span className="inline-block px-6 py-2 rounded-full text-2xl sm:text-3xl md:text-4xl font-bold text-white" style={{ backgroundColor: '#5CC1B3' }}>
                <EditableText 
                  value={galleryTitle} 
                  onChange={setGalleryTitle} 
                  className="text-2xl sm:text-3xl md:text-4xl font-bold text-white"
                  tag="span"
                  isEditMode={isEditMode}
                />
              </span>
            </h2>
            <p className="text-base sm:text-lg text-black px-2">
              <EditableText 
                value={galleryDesc} 
                onChange={setGalleryDesc} 
                className="text-base sm:text-lg text-black"
                tag="span"
                isEditMode={isEditMode}
              />
            </p>
          </div>
          
          {loadingGallery ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-light dark:text-muted-dark">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-3"></div>
              <p className="text-sm">Carregando galeria...</p>
            </div>
          ) : (
            <>
              <div ref={galleryScrollRef} className="overflow-x-auto scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
                <div className="flex gap-2 sm:gap-2 md:gap-3">
                  {[
                    ...galleryImages.map((photo, i) => ({ type: 'photo', data: photo, key: `photo-${photo.id}-${i}` })),
                    ...(galleryImages.length < placeholderGallery.length
                      ? placeholderGallery.slice(galleryImages.length).map((p, i) => ({ type: 'placeholder', data: p, key: `placeholder-${galleryImages.length + i}` }))
                      : []
                    )
                  ].map((item) => {
                    if (item.type === 'photo') {
                      const photo = item.data;
                      return (
                        <div 
                          key={item.key}
                          className="flex-shrink-0 w-20 sm:w-24 md:w-28 lg:w-32 aspect-[3/5] rounded-lg overflow-hidden hover:scale-105 transition-transform duration-300 cursor-pointer border-2 shadow-md hover:shadow-xl"
                          style={{ borderColor: 'rgba(92, 193, 179, 0.6)' }}
                          onClick={() => {
                            if (!isEditMode) {
                              handleGalleryImageClick(photo);
                            }
                          }}
                        >
                          <img src={photo.url} alt="" className="w-full h-full object-cover" />
                        </div>
                      );
                    }
                    const placeholder = item.data;
                    return (
                      <div 
                        key={item.key}
                        className="flex-shrink-0 w-20 sm:w-24 md:w-28 lg:w-32 aspect-[3/5] rounded-lg flex items-center justify-center text-4xl sm:text-5xl md:text-6xl hover:scale-105 transition-transform duration-300 cursor-pointer border-2"
                        style={{ 
                          background: 'linear-gradient(to bottom right, rgba(92, 193, 179, 0.3), rgba(92, 193, 179, 0.5))',
                          borderColor: 'rgba(92, 193, 179, 0.6)'
                        }}
                        title={placeholder.alt}
                      >
                        {placeholder.emoji}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="hidden md:flex justify-center gap-4 mt-4">
                <button
                  onClick={scrollGalleryLeft}
                  className="flex items-center justify-center w-12 h-12 rounded-full bg-primary-600 text-white hover:bg-primary-700 transition-colors shadow-lg"
                  style={{ backgroundColor: '#5CC1B3' }}
                  aria-label="Rolar para a esquerda"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={scrollGalleryRight}
                  className="flex items-center justify-center w-12 h-12 rounded-full bg-primary-600 text-white hover:bg-primary-700 transition-colors shadow-lg"
                  style={{ backgroundColor: '#5CC1B3' }}
                  aria-label="Rolar para a direita"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Qualificações */}
      <section className="py-12 sm:py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="mb-4 px-2">
              <span className="inline-block px-6 py-2 rounded-full text-2xl sm:text-3xl md:text-4xl font-bold text-white" style={{ backgroundColor: '#5CC1B3' }}>
                <EditableText 
                  value={qualificationsTitle} 
                  onChange={setQualificationsTitle} 
                  className="text-2xl sm:text-3xl md:text-4xl font-bold text-white"
                  tag="span"
                  isEditMode={isEditMode}
                />
              </span>
            </h2>
            <div className="text-base sm:text-lg text-black max-w-2xl mx-auto px-2">
              <EditableText 
                value={qualificationsDesc} 
                onChange={setQualificationsDesc} 
                className="text-base sm:text-lg text-black"
                tag="p"
                isEditMode={isEditMode}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {qualifications.map((qual, index) => {
              const Icon = qualificationIcons[index];
              return (
                <div 
                  key={index}
                  className="rounded-lg shadow-sm border border-border-light dark:border-border-dark p-6 text-center hover:shadow-lg transition-shadow duration-300"
                  style={{ backgroundColor: '#F4F7F6' }}
                >
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#5CC1B3' }}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-black mb-2">
                    <EditableText 
                      value={qual.title} 
                      onChange={(value) => updateQualification(index, 'title', value)} 
                      className="text-lg font-bold text-black"
                      tag="span"
                  isEditMode={isEditMode}
                    />
                  </h3>
                  <div className="text-sm text-black">
                    <EditableText 
                      value={qual.description} 
                      onChange={(value) => updateQualification(index, 'description', value)} 
                      className="text-sm text-black"
                      tag="p"
                  isEditMode={isEditMode}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Depoimentos */}
      <section className="py-12 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="mb-4 px-2">
              <span className="inline-block px-6 py-2 rounded-full text-2xl sm:text-3xl md:text-4xl font-bold text-white" style={{ backgroundColor: '#5CC1B3' }}>
                <EditableText 
                  value={testimonialsTitle} 
                  onChange={setTestimonialsTitle} 
                  className="text-2xl sm:text-3xl md:text-4xl font-bold text-white"
                  tag="span"
                  isEditMode={isEditMode}
                />
              </span>
            </h2>
            <div className="text-base sm:text-lg text-black px-2">
              <EditableText 
                value={testimonialsDesc} 
                onChange={setTestimonialsDesc} 
                className="text-base sm:text-lg text-black"
                tag="p"
                isEditMode={isEditMode}
              />
            </div>
          </div>
          
          <div className="relative">
            <div className="bg-[#999999] rounded-lg shadow-sm border-2 p-4 sm:p-6 md:p-8" style={{ 
              background: 'linear-gradient(to bottom right, rgba(92, 193, 179, 0.2), rgba(92, 193, 179, 0.3))',
              borderColor: 'rgba(92, 193, 179, 0.5)'
            }}>
              <div className="text-center space-y-4">
                <div className="text-4xl sm:text-5xl md:text-6xl mb-4">
                  {testimonialEmojis[activeTestimonial]}
                </div>
                <div className="flex justify-center space-x-1 mb-4">
                  {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <div className="text-base sm:text-lg italic text-black mb-4 px-2">
                  "<EditableText 
                    value={testimonials[activeTestimonial].text} 
                    onChange={(value) => updateTestimonial(activeTestimonial, 'text', value)} 
                    className="text-base sm:text-lg italic text-black"
                    tag="span"
                    multiline={3}
                    isEditMode={isEditMode}
                  />"
                </div>
                <div>
                  <p className="font-bold text-black">
                    <EditableText 
                      value={testimonials[activeTestimonial].nome} 
                      onChange={(value) => updateTestimonial(activeTestimonial, 'nome', value)} 
                      className="font-bold text-black"
                      tag="span"
                  isEditMode={isEditMode}
                    />
                  </p>
                </div>
              </div>
              
              {/* Indicadores */}
              <div className="flex justify-center space-x-2 mt-6">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      if (!isEditMode) {
                        setActiveTestimonial(index);
                      }
                    }}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === activeTestimonial 
                        ? 'w-8' 
                        : ''
                    }`}
                    style={{ backgroundColor: index === activeTestimonial ? '#5CC1B3' : 'rgba(92, 193, 179, 0.3)' }}
                    aria-label={`Ver depoimento ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sobre a Proprietária */}
      <section className="py-12 sm:py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="order-2 md:order-1">
              <h2 className="mb-4 sm:mb-6 px-2">
                <span className="inline-block px-6 py-2 rounded-full text-2xl sm:text-3xl md:text-4xl font-bold text-white" style={{ backgroundColor: '#5CC1B3' }}>
                  <EditableText 
                    value={ownerName} 
                    onChange={setOwnerName} 
                    className="text-2xl sm:text-3xl md:text-4xl font-bold text-white"
                    tag="span"
                    isEditMode={isEditMode}
                  />
                </span>
              </h2>
              <p className="text-base sm:text-lg text-black font-medium mb-4 px-2">
                <EditableText 
                  value={ownerRole} 
                  onChange={setOwnerRole} 
                  className="text-base sm:text-lg text-black font-medium"
                      tag="span"
                      isEditMode={isEditMode}
                      />
              </p>
              <div className="text-base sm:text-lg text-black mb-6 px-2">
                <EditableText 
                  value={ownerBio} 
                  onChange={setOwnerBio} 
                  className="text-base sm:text-lg text-black"
                  tag="p"
                  multiline={4}
                  isEditMode={isEditMode}
                />
              </div>
              <div className="space-y-3">
                {certifications.map((cert, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Award className="w-6 h-6 flex-shrink-0 mt-1" style={{ color: '#5CC1B3' }} />
                    <div className="flex-1">
                      <p className="font-semibold text-black">
                        <EditableText 
                          value={cert.title} 
                          onChange={(value) => updateCertification(index, 'title', value)} 
                          className="font-semibold text-black"
                      tag="span"
                      isEditMode={isEditMode}
                      />
                      </p>
                      <div className="text-sm text-black">
                        <EditableText 
                          value={cert.institution} 
                          onChange={(value) => updateCertification(index, 'institution', value)} 
                          className="text-sm text-black"
                      tag="p"
                      isEditMode={isEditMode}
                      />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="relative max-w-sm mx-auto">
                {ownerPhoto ? (
                  <div className="relative aspect-square rounded-2xl overflow-hidden border-4 shadow-2xl" style={{ 
                    borderColor: 'rgba(92, 193, 179, 0.8)'
                  }}>
                    <img 
                      src={ownerPhoto} 
                      alt={ownerName}
                      className="w-full h-full object-cover"
                    />
                    {isEditMode && (
                      <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center group">
                        <button
                          onClick={handleOwnerPhotoRemove}
                          className="opacity-0 group-hover:opacity-100 bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-opacity duration-200 flex items-center space-x-2"
                        >
                          <X className="w-4 h-4" />
                          <span>Remover Foto</span>
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="aspect-square rounded-2xl flex items-center justify-center text-6xl sm:text-7xl md:text-9xl border-4 shadow-2xl" style={{ 
                    background: 'linear-gradient(to bottom right, rgba(92, 193, 179, 0.4), rgba(92, 193, 179, 0.7))',
                    borderColor: 'rgba(92, 193, 179, 0.8)'
                  }}>
                    👩‍⚕️
                  </div>
                )}
                {isEditMode && (
                  <div className="mt-4 flex justify-center">
                    <label className="cursor-pointer bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center space-x-2">
                      {uploadingPhoto ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Enviando...</span>
                        </>
                      ) : (
                        <>
                          <User className="w-4 h-4" />
                          <span>{ownerPhoto ? 'Alterar Foto' : 'Adicionar Foto'}</span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleOwnerPhotoUpload}
                        className="hidden"
                        disabled={uploadingPhoto}
                      />
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action - Agendamento */}
      <section id="agendar" className="py-12 sm:py-16 px-4 text-white" style={{ background: 'linear-gradient(to right, #5CC1B3, #4AA89E)' }}>
        <div className="max-w-4xl mx-auto text-center space-y-4 sm:space-y-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 px-2">
            <EditableText 
              value={ctaTitle} 
              onChange={setCtaTitle} 
              className="text-2xl sm:text-3xl md:text-4xl font-bold"
                      tag="span"
                      isEditMode={isEditMode}
                      />
          </h2>
          <div className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 text-primary-100 px-4">
            <EditableText 
              value={ctaDesc} 
              onChange={setCtaDesc} 
              className="text-base sm:text-lg md:text-xl text-primary-100"
                      tag="p"
                      isEditMode={isEditMode}
                      />
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
            <button
              onClick={() => {
                if (!isEditMode) {
                  handleOpenWhatsAppModal();
                }
              }}
              className="w-full sm:w-auto bg-white text-primary-600 px-8 sm:px-10 py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg hover:bg-primary-50 transition-all duration-200 shadow-xl hover:shadow-2xl flex items-center justify-center space-x-2"
            >
              <Calendar className="w-5 h-5 sm:w-6 sm:h-6" />
              <span>Agendar Agora</span>
            </button>
          </div>
        </div>
      </section>

      {/* Informações de Contato */}
      <section className="py-12 sm:py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="mb-4 px-2">
              <span className="inline-block px-6 py-2 rounded-full text-2xl sm:text-3xl md:text-4xl font-bold text-white" style={{ backgroundColor: '#5CC1B3' }}>
                <EditableText 
                  value={contactTitle} 
                  onChange={setContactTitle} 
                  className="text-2xl sm:text-3xl md:text-4xl font-bold text-white"
                  tag="span"
                  isEditMode={isEditMode}
                />
              </span>
            </h2>
            <p className="text-base sm:text-lg text-black px-2">
              <EditableText 
                value={contactDesc} 
                onChange={setContactDesc} 
                className="text-base sm:text-lg text-black"
                tag="span"
                isEditMode={isEditMode}
              />
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            <div 
              onClick={() => {
                if (!isEditMode) {
                  handleOpenWhatsApp();
                }
              }}
              className="rounded-lg shadow-sm border border-border-light dark:border-border-dark p-6 text-center hover:shadow-lg transition-all cursor-pointer hover:scale-105"
              style={{ backgroundColor: '#F4F7F6' }}
            >
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#5CC1B3' }}>
                <MessageCircle className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-bold text-black mb-2">WhatsApp</h3>
              <div className="text-black">
                <EditableText 
                  value={contactPhone} 
                  onChange={(value) => setContactPhone(formatDisplayPhone(value))} 
                  className="text-black"
                      tag="span"
                      isEditMode={isEditMode}
                      />
              </div>
            </div>
            <div 
              onClick={() => {
                if (!isEditMode) {
                  handleOpenInstagram();
                }
              }}
              className="rounded-lg shadow-sm border border-border-light dark:border-border-dark p-6 text-center hover:shadow-lg transition-all cursor-pointer hover:scale-105"
              style={{ backgroundColor: '#F4F7F6' }}
            >
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#5CC1B3' }}>
                <Instagram className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-bold text-black mb-2">Instagram</h3>
              <div className="text-black">
                <EditableText 
                  value={contactEmail} 
                  onChange={setContactEmail} 
                  className="text-black"
                      tag="span"
                      isEditMode={isEditMode}
                      />
              </div>
            </div>
            <div 
              onClick={() => {
                if (!isEditMode) {
                  handleOpenGoogleMaps();
                }
              }}
              className="rounded-lg shadow-sm border border-border-light dark:border-border-dark p-6 text-center hover:shadow-lg transition-all cursor-pointer hover:scale-105"
              style={{ backgroundColor: '#F4F7F6' }}
            >
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#5CC1B3' }}>
                <MapPin className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-bold text-black mb-2">Endereço</h3>
              <div className="text-black">
                <EditableText 
                  value={contactAddress} 
                  onChange={setContactAddress} 
                  className="text-black"
                  tag="p"
                  multiline={2}
                  isEditMode={isEditMode}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Horário de Funcionamento */}
      <section className="py-12 sm:py-16 px-4" style={{ backgroundColor: '#BFC9C8' }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="mb-4 px-2">
              <span className="inline-block px-6 py-2 rounded-full text-2xl sm:text-3xl md:text-4xl font-bold text-white" style={{ backgroundColor: '#5CC1B3' }}>
                <EditableText 
                  value={openingHoursTitle} 
                  onChange={setOpeningHoursTitle} 
                  className="text-2xl sm:text-3xl md:text-4xl font-bold"
                  tag="span"
                  isEditMode={isEditMode}
                />
              </span>
            </h2>
          </div>
          
          <div className="flex flex-col sm:flex-row flex-wrap justify-center items-center gap-4 sm:gap-6">
            {openingHours.map((schedule, index) => (
              <div 
                key={index}
                className="inline-block px-6 py-3 rounded-full text-center"
                style={{ backgroundColor: 'white' }}
              >
                <div className="flex flex-col sm:flex-row items-center gap-2">
                  <span className="font-semibold text-sm sm:text-base" style={{ color: '#104347' }}>
                    <EditableText 
                      value={schedule.days} 
                      onChange={(value) => updateOpeningHour(index, 'days', value)} 
                      className="font-semibold inline text-sm sm:text-base"
                      tag="span"
                      isEditMode={isEditMode}
                    />
                  </span>
                  <span className="font-bold text-sm sm:text-base" style={{ color: '#104347' }}>
                    <EditableText 
                      value={schedule.hours} 
                      onChange={(value) => updateOpeningHour(index, 'hours', value)} 
                      className="font-bold inline text-sm sm:text-base"
                      tag="span"
                      isEditMode={isEditMode}
                    />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Parceiros/Colaboradores */}
      <section className="py-12 sm:py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="mb-4 px-2">
              <span className="inline-block px-6 py-2 rounded-full text-2xl sm:text-3xl md:text-4xl font-bold text-white" style={{ backgroundColor: '#5CC1B3' }}>
                <EditableText 
                  value={partnersTitle} 
                  onChange={setPartnersTitle} 
                  className="text-2xl sm:text-3xl md:text-4xl font-bold text-white"
                  tag="span"
                  isEditMode={isEditMode}
                />
              </span>
            </h2>
            <p className="text-base sm:text-lg text-black px-2">
              <EditableText 
                value={partnersDesc} 
                onChange={setPartnersDesc} 
                className="text-base sm:text-lg text-black"
                tag="span"
                isEditMode={isEditMode}
              />
            </p>
          </div>

          {isEditMode && (
            <div className="mb-6 flex justify-center">
              <button
                onClick={addPartner}
                className="bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center space-x-2"
                style={{ backgroundColor: '#5CC1B3' }}
              >
                <Users className="w-5 h-5" />
                <span>Adicionar Parceiro</span>
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {partners.map((partner, index) => (
              <div 
                key={index}
                className="rounded-lg shadow-lg border-2 p-6 text-center hover:shadow-xl transition-all duration-300 bg-white"
                style={{ 
                  borderColor: 'rgba(92, 193, 179, 0.5)'
                }}
              >
                <div className="mx-auto mb-4" style={{ maxWidth: '180px' }}>
                  <div className={`relative${isEditMode ? ' mb-6' : ''}`}>
                  {partner.logo ? (
                    <div className="relative aspect-video rounded-lg overflow-hidden border-2 shadow-md bg-white p-3 flex items-center justify-center" style={{ 
                      borderColor: 'rgba(92, 193, 179, 0.5)'
                    }}>
                      <img 
                        src={partner.logo} 
                        alt={partner.nome}
                        className="w-full h-full object-contain"
                      />
                      {isEditMode && (
                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center group rounded-lg">
                          <button
                            onClick={() => handlePartnerLogoRemove(index)}
                            className="opacity-0 group-hover:opacity-100 bg-red-600 text-white px-3 py-1 rounded-lg text-sm font-medium transition-opacity duration-200 flex items-center space-x-1"
                          >
                            <X className="w-3 h-3" />
                            <span>Remover</span>
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="relative aspect-video rounded-lg flex items-center justify-center text-4xl sm:text-5xl border-2 shadow-md bg-gray-50" style={{ 
                      borderColor: 'rgba(92, 193, 179, 0.5)'
                    }}>
                      🏢
                    </div>
                  )}
                  {isEditMode && (
                    <label className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 z-10 cursor-pointer bg-primary-600 text-white px-3 py-1 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors flex items-center space-x-1 shadow-md whitespace-nowrap">
                      {uploadingPartnerLogo[index] ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          <span>Enviando...</span>
                        </>
                      ) : (
                        <>
                          <Package className="w-3 h-3" />
                          <span>{partner.logo ? 'Alterar Foto' : 'Adicionar Foto'}</span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handlePartnerLogoUpload(e, index)}
                        className="hidden"
                        disabled={uploadingPartnerLogo[index]}
                      />
                    </label>
                  )}
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-black mb-2">
                  <EditableText 
                    value={partner.nome} 
                    onChange={(value) => updatePartner(index, 'nome', value)} 
                    className="text-xl font-bold text-black"
                    tag="span"
                    isEditMode={isEditMode}
                  />
                </h3>
                <p className="text-base font-medium mb-3" style={{ color: '#5CC1B3' }}>
                  <EditableText 
                    value={partner.tipo} 
                    onChange={(value) => updatePartner(index, 'tipo', value)} 
                    className="text-base font-medium"
                    tag="span"
                    isEditMode={isEditMode}
                  />
                </p>
                <div className="text-sm text-black mb-3">
                  <EditableText 
                    value={partner.descricao} 
                    onChange={(value) => updatePartner(index, 'descricao', value)} 
                    className="text-sm text-black"
                    tag="p"
                    multiline={3}
                    isEditMode={isEditMode}
                  />
                </div>
                
                {!isEditMode && partner.site && (
                  <a
                    href={partner.site}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                    style={{ color: '#5CC1B3' }}
                  >
                    <span>Visitar site</span>
                    <ArrowRight className="w-3 h-3" />
                  </a>
                )}
                
                {isEditMode && (
                  <div className="mt-4 pt-4 border-t border-gray-300 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Site (URL)</label>
                      <EditableText 
                        value={partner.site || ''} 
                        onChange={(value) => updatePartner(index, 'site', value)} 
                        className="text-sm text-black w-full px-2 py-1 border border-gray-300 rounded"
                        tag="input"
                        isEditMode={isEditMode}
                      />
                    </div>
                    <button
                      onClick={() => removePartner(index)}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex items-center space-x-2 mx-auto"
                    >
                      <X className="w-4 h-4" />
                      <span>Remover Parceiro</span>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="pt-12 sm:pt-16 pb-4 sm:pb-6 px-4 text-white" style={{ backgroundColor: '#5CC1B3' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="mb-4 px-2">
              <button
                onClick={() => {
                  if (!isEditMode) {
                    navigate('/blog');
                  }
                }}
                className="inline-block px-6 py-3 rounded-lg text-2xl sm:text-3xl md:text-4xl font-bold bg-white text-primary-600 shadow-lg hover:bg-primary-50 transition-all duration-200 cursor-pointer"
              >
                {isEditMode ? (
                  <EditableText 
                    value={blogTitle} 
                    onChange={setBlogTitle} 
                    className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary-600"
                    tag="span"
                    isEditMode={isEditMode}
                    inputClassName="bg-white/10 border-2 border-white/30 rounded px-2 py-1 focus:outline-none focus:border-white/60"
                  />
                ) : (
                  <>
                    <span>{blogTitle.split(' ')[0]} </span>
                    <span style={{ fontFamily: 'Playfair Display, serif' }}>{blogTitle.split(' ').slice(1).join(' ')}</span>
                  </>
                )}
              </button>
            </h2>
            <div className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 text-primary-100 px-4">
              <EditableText 
                value={blogDesc} 
                onChange={setBlogDesc} 
                className="text-base sm:text-lg md:text-xl text-primary-100"
                tag="span"
                isEditMode={isEditMode}
                inputClassName="bg-white/10 border-2 border-white/30 rounded px-2 py-1 focus:outline-none focus:border-white/60"
              />
            </div>
          </div>

          {loadingBlog ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-3"></div>
              <p className="text-sm text-muted-light dark:text-muted-dark">Carregando posts...</p>
            </div>
          ) : blogPosts.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-8">
                {blogPosts.map((post) => (
                  <article
                    key={post.id}
                    className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-200 group"
                    onClick={() => {
                      if (!isEditMode) {
                        navigate(`/blog/${post.id}`);
                      }
                    }}
                  >
                    {(post.urlImagem || post.imageUrl) && (
                      <div className="aspect-video overflow-hidden bg-gray-200">
                        <img
                          src={post.urlImagem || post.imageUrl}
                          alt={post.titulo || post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-lg font-bold text-black mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {post.titulo || post.title}
                      </h3>
                      
                      {(post.resumo || post.excerpt) && (
                        <p className="text-sm text-gray-700 mb-4 line-clamp-3">
                          {post.resumo || post.excerpt}
                        </p>
                      )}
                      
                      <div className="flex items-center justify-between">
                        {post.criadoEm && (
                          <div className="flex items-center gap-1 text-xs text-gray-600">
                            <Calendar className="w-3 h-3" />
                            <span>
                              {post.criadoEm.toDate ? 
                                post.criadoEm.toDate().toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }) :
                                new Date(post.criadoEm).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
                              }
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-sm font-medium text-primary-600">
                          <span>Ler mais</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
              
              <div className="flex justify-center">
                <button
                  onClick={() => {
                    if (!isEditMode) {
                      navigate('/blog');
                    }
                  }}
                  className="w-full sm:w-auto bg-white text-primary-600 px-6 sm:px-8 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg"
                >
                  <BookOpen className="w-5 h-5" />
                  <span>Ver todos os posts</span>
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-10">
              <BookOpen className="w-16 h-16 mx-auto mb-8 text-muted-light dark:text-muted-dark" />
              <p className="text-lg text-muted-light dark:text-muted-dark mb-2">
                Em breve teremos conteúdo interessante para você!
              </p>
              <div className="flex justify-center">
                <button
                  onClick={() => {
                    if (!isEditMode) {
                      navigate('/blog');
                    }
                  }}
                  className="w-full sm:w-auto bg-white text-primary-600 px-6 sm:px-8 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg mt-6"
                >
                  <BookOpen className="w-5 h-5" />
                  <span>Visitar Blog</span>
                </button>
              </div>
            </div>
          )}
          
          <div className="border-t border-black pt-4 sm:pt-6 mt-10 sm:mt-10 pb-0 text-center">
            <p className="text-sm text-muted-light dark:text-muted-dark">ArukDog</p>
          </div>
        </div>
      </section>

      {/* Modal de Confirmação */}
      {/* Modal de Crop de Imagem */}
      {showCropModal && (imageToCrop || partnerImageToCrop) && (
        <ImageCropModal
          isOpen={showCropModal}
          onClose={handleCropCancel}
          imageFile={imageToCrop || partnerImageToCrop}
          onCropComplete={handleCropComplete}
          aspectRatio={1} // Quadrado para logos/fotos
        />
      )}

      <ConfirmationModal
        isOpen={confirmation.isOpen}
        onConfirm={confirmAction}
        onCancel={cancelAction}
        title={confirmation.title}
        message={confirmation.message}
        confirmText={confirmation.confirmText}
        cancelText={confirmation.cancelText}
        showCancel={confirmation.showCancel}
        type={confirmation.type}
      />

      {/* Modal de WhatsApp */}
      <WhatsAppModal 
        isOpen={showWhatsAppModal}
        onClose={() => setShowWhatsAppModal(false)}
        businessPhone={formatWhatsAppNumber(contactPhone)}
      />

      {/* Modal de Visualização da Galeria */}
      <GalleryImageModal
        isOpen={showGalleryModal}
        onClose={closeGalleryModal}
        selectedImage={selectedGalleryImage}
        images={galleryImages}
        onNavigate={navigateGalleryImage}
        onImageClick={user && selectedGalleryImage?.id ? () => { closeGalleryModal(); navigate(`/pets/${selectedGalleryImage.id}`); } : undefined}
        imageClickTitle={user && selectedGalleryImage?.id ? 'Ver página do pet' : undefined}
        imageAlt={`Foto de ${selectedGalleryImage?.nome || ''} em tamanho expandido`}
      />
    </div>
  );
};

export default Home;

